import { useState } from "react";
import Style from "./syncProgress.module.scss";
import useSyncProgress from "./useSyncProgress";
import {
  needsApprovalOf,
  SYNC_BAR_STATE,
  SYNC_STATUS_CLASS,
  SYNC_STATUS_LABEL,
} from "../../../../utils/Functions/posistSync";

const Bar = ({ percent, tone, large }) => (
  <div
    className={`${Style.bar} ${large ? Style.bar_lg : ""} ${
      Style[tone] ?? ""
    }`}
    role="progressbar"
    aria-valuenow={percent}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div className={Style.fill} style={{ width: `${percent}%` }} />
  </div>
);

const StoreRow = ({ store }) => {
  const status = store?.status ?? "pending";
  const { percent, tone } = SYNC_BAR_STATE[status] ?? SYNC_BAR_STATE.pending;

  return (
    <div className={Style.store_row}>
      <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-1">
        <span className="pro-font-sm pro-fw-medium">{store?.store_name}</span>
        <span className={`pro-font-sm ${SYNC_STATUS_CLASS[status] ?? ""}`}>
          {SYNC_STATUS_LABEL[status] ?? status}
        </span>
      </div>

      <Bar percent={percent} tone={tone} />

      {/* Only once the store is done - a running store has partial counters
          on its row, and showing those would have the numbers climbing while
          the admin reads them. */}
      {status === "success" && (
        <div className="pro-font-xs pro-text-muted pro-mt-1">
          {`${store?.changed_items ?? 0} changed items → ${
            store?.staged ?? 0
          } compared`}
          {(store?.deleted ?? 0) > 0 && `, ${store.deleted} delisted`}
          {", "}
          <span className="pro-fw-medium">
            {`${needsApprovalOf(store)} need approval`}
          </span>
        </div>
      )}

      {(status === "failed" || status === "skipped") && store?.message && (
        <div className="pro-font-xs pro-text-muted pro-mt-1">
          {store.message}
        </div>
      )}
    </div>
  );
};

/**
 * The menu sync in flight, while it is in flight.
 *
 * Renders nothing at all when nothing is syncing - the panel is not a history
 * of the last run, it is the thing you watch while one is going, and it takes
 * itself away once every store is done. That also means an admin who never
 * runs a sync, or has no permission to ask, sees a dashboard unchanged from
 * before this existed.
 */
const SyncProgress = () => {
  const { run, otherActiveRuns, finishedCount, total, percent, failedCount } =
    useSyncProgress();

  // Collapsed by default: the run-level bar is the thing being watched, and a
  // twelve-store run would otherwise push the rest of the dashboard off the
  // screen for the few minutes it takes.
  const [expanded, setExpanded] = useState(false);

  if (!run) return null;

  return (
    <div className="pro-w-100 pro-my-4 pro-px-5">
      <div className="row g-5">
        <div className="col-12">
          <div className={Style.data_container}>
            <div className={Style.header}>
              <h6 className="pro-ttl h6 pro-mb-0">Menu Sync</h6>
              <span className="pro-font-sm pro-text-muted">
                {`${finishedCount} of ${total} store${
                  total === 1 ? "" : "s"
                } complete`}
              </span>
            </div>

            <div className={Style.body}>
              <Bar percent={percent} tone="idle" large />

              <button
                type="button"
                className={`pro-btn ${Style.toggle} pro-mt-3`}
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                aria-controls="menu-sync-stores"
              >
                <span
                  className={`material-symbols-outlined ${Style.chevron} ${
                    expanded ? Style.open : ""
                  }`}
                  aria-hidden="true"
                >
                  chevron_right
                </span>
                <span>{expanded ? "Hide stores" : "Show stores"}</span>
                {/* Surfaced on the collapsed row rather than left for whoever
                    thinks to expand: a panel that hid a failure behind a click
                    and then took itself away when the run ended would be the
                    one way this screen could lose something that matters. */}
                {failedCount > 0 && (
                  <span className="pro-text-danger pro-ms-2">
                    {`${failedCount} failed`}
                  </span>
                )}
              </button>

              {expanded && (
                <div id="menu-sync-stores" className="pro-mt-3">
                  {(run?.stores ?? []).map((store) => (
                    <StoreRow key={store?.customer_key} store={store} />
                  ))}

                  {/* Rare - runs are started by hand and take about a minute a
                      store - but a second one is still work the worker is
                      doing, and a panel that showed one run as if it were
                      everything would understate what is happening to the
                      menu. */}
                  {otherActiveRuns > 0 && (
                    <div className="pro-font-xs pro-text-muted pro-mt-4">
                      {`${otherActiveRuns} other sync run${
                        otherActiveRuns === 1 ? " is" : "s are"
                      } also in progress and not shown here.`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncProgress;
